import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import api, { Response } from '../../utils/api'
import auth from '../../utils/auth'
import { RootState } from '../../store'
import { getRefreshToken, isTokenExpired } from '../../utils/jwt'
import { UserType } from './types'

export interface AuthState {
    readonly isLoadingAuth: boolean
    readonly isLoggedIn: boolean
    readonly user?: UserType
}

const initAuthState: AuthState = {
    isLoadingAuth: true,
    isLoggedIn: false,
    user: undefined,
}

export const getCurrentUser = createAsyncThunk(
    'getCurrentUser',
    async (params, thunkAPI) => {
        const refreshToken = getRefreshToken()
        if (!refreshToken || isTokenExpired(refreshToken)) {
            console.warn('getCurrentUser: Refresh token is missing or expired')
            return thunkAPI.rejectWithValue('Refresh Token is expired')
        }
        
        const response = await api.get<UserType>('/users/current/')
        
        // Handle specific error cases more gracefully
        if (response.code === 401 || response.code === 403) {
            console.warn('getCurrentUser: Authentication failed with status', response.code)
            return thunkAPI.rejectWithValue('Authentication failed')
        }
        
        if (response.error && response.code >= 400) {
            console.warn('getCurrentUser: API error', response.code, response.error)
            // For non-auth errors, don't automatically logout - could be server issues
            if (response.code >= 500) {
                return thunkAPI.rejectWithValue('Server error - retaining session')
            }
            return thunkAPI.rejectWithValue(response.error)
        }
        
        if (!response.data?.id) {
            console.warn('getCurrentUser: Invalid user data received')
            return thunkAPI.rejectWithValue('Invalid user data')
        }
        
        return response
    },
)

export const logout = createAsyncThunk(
    'LOGOUT_USER',
    auth.logout,
)

const setUser = (state: AuthState, action: PayloadAction<Response<UserType>>): AuthState => (
    !action?.payload.data?.id
        ? onUnauthenticated()
        : ({
            isLoadingAuth: false,
            isLoggedIn: true,
            user: action.payload.data,
        })
)

const onUnauthenticated = (): AuthState => {
    console.warn('User being logged out (onUnauthenticated called)')
    console.trace('Logout stack trace:')
    return {
        isLoadingAuth: false,
        isLoggedIn: false,
        user: undefined,
    }
}

const authSlice = createSlice({
    name: 'auth',
    initialState: initAuthState,
    reducers: {
        removeUser: () => onUnauthenticated(),
    },
    extraReducers: (builder) => {
        builder.addCase(getCurrentUser.fulfilled, (state, action) => setUser(state, action))
        builder.addCase(getCurrentUser.rejected, (state, action) => {
            console.warn('getCurrentUser rejected:', action.payload)
            
            // Only logout for authentication-related failures, not server errors
            if (action.payload === 'Server error - retaining session') {
                console.log('Retaining user session despite server error')
                return {
                    ...state,
                    isLoadingAuth: false,
                    // Keep user logged in for server errors
                }
            }
            
            return onUnauthenticated()
        })
        builder.addCase(logout.fulfilled, () => {
            console.log('User logged out via logout action')
            // No alert needed for manual logout as user initiated this action
            return onUnauthenticated()
        })
    },
})

export const selectUser = (state: RootState) => state.auth.user
export const selectIsAuthenticated = (state: RootState) => state.auth.isLoggedIn
export const selectIsLoadingAuth = (state: RootState) => state.auth.isLoadingAuth
export const removeCurrentUser = authSlice.actions.removeUser
export default authSlice.reducer
